import React, {useEffect, useState, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import searchIcon from '../../assets/search-glass.png';
import PageBase from "../../components/PageBase";
import Datatable from "../../components/Datatable";
import * as fundActions from "../../redux/Funds/actions";
import Filters from "../../components/Filters";
import availableFilters from './availableFilters.js';
import columns from './column.js';
import LoadingPanel from "../../components/LoadingPanel";
import Icon from 'react-web-vector-icons';
import moment from "moment/moment.js";
import constants from "../../utils/constants.js";
import usePersistentFilters from '../../hooks/usePersistentFilters';
import useColumnFilters from '../../hooks/useColumnFilters';
import { getDisplayFields, getDefaultFields } from '../../utils/fields/funds.js';
import ListManager from '../../components/ListManager';
import {createColumnConfigs} from '../../utils/fieldUtils.js';

import './funds.scss';

const Funds = props => {
	const [loading, setLoading] = useState(true);
	const [filterResetKey, setFilterResetKey] = useState(0);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const funds = useSelector(state => state.fund).funds;
	const lastUpdated = useSelector(state => state.fund).lastUpdated;
	const [listColumns, setListColumns] = useState([]);
	const entityType = `${constants.DISPLAY_ENTITIES.FUNDS}`;
		const fieldsToUse = useMemo(() => {
		return createColumnConfigs(getDisplayFields(listColumns.length === 0,listColumns))
		}, [listColumns]);

	const {
		search,
		setSearch,
		selectedFilters,
		setSelectedFilters,
		clearFilters: clearPersistentFilters
	} = usePersistentFilters('funds', '', []);
	
	const searchedData = useMemo(() => {
	if (!funds) return [];
	if (!search) return funds;
	const lowerSearch = search.toLowerCase();
	return funds.filter(f => f.Name.toLowerCase().includes(lowerSearch));
	}, [funds, search]);

	const {
		filters,
		sortBy,
		sortOrder,
		filteredData,
		handleFilterChange,
		resetWith,
		updateData,
		uniqueValuesByColumn,
		filterOrder
	} = useColumnFilters({
		data: searchedData,
		columns: fieldsToUse,
		storageKey: 'funds',
		initialState: {}
	});

	useEffect(() => {
		if(selectedFilters.length > 0) return;
		const load = async () => {
			await dispatch(fundActions.loadFunds([]));
			setLoading(false);
		};
		load();
	}, []);

	useEffect(() => {
		if(selectedFilters.length === 0) return;	
		const load = async () => {
			setLoading(true);
			await dispatch(fundActions.loadFunds(selectedFilters));
			setLoading(false);
		};
		load();
	}, [selectedFilters]);

	// Update filtered data when funds change
	useEffect(() => {
		updateData(funds);
	}, [funds]);

	if(loading || funds == null) {
		return (
			<PageBase title="Funds" removeMargins>
				<LoadingPanel />
			</PageBase>
		)
	}

	const onRowClick = async (row, index) => {
		console.log(index, row.Id);
		navigate(`/fund/${row.Id}`);
	};

	const updateFilter = (value) => {
		setSearch(value.toLowerCase());
	};

	// const updateSort = (sortColumn) => {
	// 	if(sortColumn === sortBy) {
	// 		setSortOrder(sortOrder === "asc" ? "desc" : "asc");
	// 	}
	// 	else {
	// 		setSortBy(sortColumn);
	// 		setSortOrder("asc");
	// 	}
	// };

	const addFilter = filter => {
		const index = selectedFilters.findIndex(f => f.name === filter.name);

		if(index < 0) {
			setSelectedFilters([...selectedFilters, filter]);
		}
		else {
			const updated = [...selectedFilters];
			updated[index] = filter;
			setSelectedFilters(updated);
		}
	};

	const removeFilter = (filterToRemove) => {
		const filters = selectedFilters.filter(f => f.name !== filterToRemove.name);
		setSelectedFilters(filters);
	};

	const onClearFilters = () => {
		clearPersistentFilters();
	};

	return (
		<PageBase title="Funds" removeMargins>
			<div className="funds-header">
				<div className="funds-search-box">
					<img src={searchIcon} style={{padding: '0 10px'}} />
					<input className="funds-search-input" type="text" value={search} onChange={e => updateFilter(e.target.value)} placeholder="Search Funds..." />
				</div>
				<Filters availableFilters={availableFilters} clearFilters={onClearFilters} selectedFilters={selectedFilters} onAddFilter={addFilter} onRemoveFilter={removeFilter} />
				<div style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', paddingRight: 20 }}>
					<ListManager
						entityType={entityType}
						displayFields={getDisplayFields()}
						defaultFields={getDisplayFields(true).map(f => f.field)}
						onColumnsChange={setListColumns}
					/>
				</div>
			</div>

			{selectedFilters.length > 0 && (
				<div style={{margin: '0 30px'}}>
					<p style={{ color: '#062229', fontWeight: 700, fontSize: 14 }}>Found {filteredData?.length || 0} Funds</p>
				</div>
			)}

			<div style={{margin: '0 30px'}}>
				<Datatable 
					gridHeight="calc(100vh - 185px)"
					key={filterResetKey}
					columns={fieldsToUse}
					//onSort={updateSort}
					sortBy={sortBy}
					sortOrder={sortOrder}
					data={filteredData}
					onRowClick={onRowClick}
					filters={filters}
					onFilterChange={handleFilterChange}
					filterOrder={filterOrder}
					uniqueValuesByColumn={uniqueValuesByColumn}
					allData={funds}
				/>
			</div>
		</PageBase>
	)
};

export default Funds;
